import React from "react"
import Logo from "~/components/Icons/Logo"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import Play from "~/components/Icons/Play"
import Icons from "~/components/Icons"
import Download from "~/components/Icons/Download"
import { useEditor } from "@agenteresolve/react"
import useEditorType from "~/hooks/useEditorType"
import { IScene } from "@agenteresolve/types"
import { loadTemplateFonts } from "~/utils/fonts"
import { loadVideoEditorAssets } from "~/utils/video"
import DesignTitle from "./DesignTitle"
import { IDesign } from "~/interfaces/DesignEditor"
import { uploadTemplateThumb } from "~/utils/thumbnails"
import { useTranslation } from "react-i18next"

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
}

function IconCaret({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

function IconCamera({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1.3-1.7A1.5 1.5 0 0 1 10 4.7h4a1.5 1.5 0 0 1 1.2.6L16.5 7h2A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-9z" />
      <circle cx="12" cy="13" r="3.2" />
    </svg>
  )
}

export default function () {
  const { setDisplayPreview, setScenes, setCurrentDesign, currentDesign, scenes, setEditorType } = useDesignEditorContext()
  const editorType = useEditorType()
  const editor = useEditor()
  const inputFileRef = React.useRef<HTMLInputElement>(null)
  const [exportMenuOpen, setExportMenuOpen] = React.useState(false)
  const { t } = useTranslation("editor")

  React.useEffect(() => {
    if (!exportMenuOpen) return
    const close = () => setExportMenuOpen(false)
    window.addEventListener("click", close)
    return () => window.removeEventListener("click", close)
  }, [exportMenuOpen])

  const parseGraphicJSON = () => {
    const currentScene = editor.scene.exportToJSON()

    const updatedScenes = scenes.map((scn) => {
      if (scn.id === currentScene.id) {
        return {
          id: currentScene.id,
          layers: currentScene.layers,
          name: currentScene.name,
        }
      }
      return {
        id: scn.id,
        layers: scn.layers,
        name: scn.name,
      }
    })

    if (currentDesign) {
      const graphicTemplate: IDesign = {
        id: currentDesign.id,
        type: "GRAPHIC",
        name: currentDesign.name,
        frame: currentDesign.frame,
        scenes: updatedScenes,
        metadata: {},
        preview: "",
      }
      makeDownload(graphicTemplate)
    } else {
      console.log("NO CURRENT DESIGN")
    }
  }

  const parsePresentationJSON = () => {
    const currentScene = editor.scene.exportToJSON()

    const updatedScenes = scenes.map((scn) => {
      if (scn.id === currentScene.id) {
        return {
          id: currentScene.id,
          duration: 5000,
          layers: currentScene.layers,
          name: currentScene.name,
        }
      }
      return {
        id: scn.id,
        duration: 5000,
        layers: scn.layers,
        name: scn.name,
      }
    })

    if (currentDesign) {
      const presentationTemplate: IDesign = {
        id: currentDesign.id,
        type: "PRESENTATION",
        name: currentDesign.name,
        frame: currentDesign.frame,
        scenes: updatedScenes,
        metadata: {},
        preview: "",
      }
      makeDownload(presentationTemplate)
    } else {
      console.log("NO CURRENT DESIGN")
    }
  }

  const parseVideoJSON = () => {
    const currentScene = editor.scene.exportToJSON()
    const updatedScenes = scenes.map((scn) => {
      if (scn.id === currentScene.id) {
        return {
          id: scn.id,
          duration: scn.duration,
          layers: currentScene.layers,
          name: currentScene.name ? currentScene.name : "",
        }
      }
      return {
        id: scn.id,
        duration: scn.duration,
        layers: scn.layers,
        name: scn.name ? scn.name : "",
      }
    })
    if (currentDesign) {
      const videoTemplate: IDesign = {
        id: currentDesign.id,
        type: "VIDEO",
        name: currentDesign.name,
        frame: currentDesign.frame,
        scenes: updatedScenes,
        metadata: {},
        preview: "",
      }
      makeDownload(videoTemplate)
    } else {
      console.log("NO CURRENT DESIGN")
    }
  }

  const makeDownload = (data: Object) => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(data))
    const a = document.createElement("a")
    a.href = dataStr
    a.download = "template.json"
    a.click()
  }

  // Renders the current scene and stores it as the preview of the template
  // that is currently loaded (used by the Library listing), also downloading
  // the image so it can replace the static thumbnail file.
  const saveSnapshot = async () => {
    if (!editor) return
    try {
      const scene = editor.scene.exportToJSON()
      const preview = (await editor.renderer.render(scene)) as string
      if (!preview) return
      let activeId = ""
      try {
        activeId = localStorage.getItem("tpl-active-id") || ""
      } catch {
        // ignore storage errors
      }
      if (!activeId) activeId = currentDesign?.id || scene.id || "design"
      try {
        localStorage.setItem(`tpl-thumb:${activeId}`, preview)
      } catch {
        // ignore storage errors
      }
      await uploadTemplateThumb(activeId, preview)
      window.dispatchEvent(new Event("tpl-thumb-updated"))
      const a = document.createElement("a")
      a.href = preview
      a.download = `snapshot-${activeId}.png`
      a.click()
    } catch (err) {
      console.error("snapshot failed", err)
    }
  }

  const makeDownloadTemplate = async () => {
    if (editor) {
      if (editorType === "GRAPHIC") {
        return parseGraphicJSON()
      } else if (editorType === "PRESENTATION") {
        return parsePresentationJSON()
      } else {
        return parseVideoJSON()
      }
    }
  }

  const loadGraphicTemplate = async (payload: IDesign) => {
    const scenes = []
    const { scenes: scns, ...design } = payload

    for (const scn of scns) {
      const scene: IScene = {
        name: scn.name,
        frame: payload.frame,
        id: scn.id,
        layers: scn.layers,
        metadata: {},
      }
      const loadedScene = await loadVideoEditorAssets(scene)
      await loadTemplateFonts(loadedScene)

      const preview = (await editor.renderer.render(loadedScene)) as string
      scenes.push({ ...loadedScene, preview })
    }

    return { scenes, design }
  }

  const loadPresentationTemplate = async (payload: IDesign) => {
    const scenes = []
    const { scenes: scns, ...design } = payload

    for (const scn of scns) {
      const scene: IScene = {
        name: scn.name,
        frame: payload.frame,
        id: scn,
        layers: scn.layers,
        metadata: {},
      }
      const loadedScene = await loadVideoEditorAssets(scene)

      const preview = (await editor.renderer.render(loadedScene)) as string
      await loadTemplateFonts(loadedScene)
      scenes.push({ ...loadedScene, preview })
    }
    return { scenes, design }
  }

  const loadVideoTemplate = async (payload: IDesign) => {
    const scenes = []
    const { scenes: scns, ...design } = payload

    for (const scn of scns) {
      const design: IScene = {
        name: "Awesome template",
        frame: payload.frame,
        id: scn.id,
        layers: scn.layers,
        metadata: {},
        duration: scn.duration,
      }
      const loadedScene = await loadVideoEditorAssets(design)

      const preview = (await editor.renderer.render(loadedScene)) as string
      await loadTemplateFonts(loadedScene)
      scenes.push({ ...loadedScene, preview })
    }
    return { scenes, design }
  }

  const handleImportTemplate = React.useCallback(
    async (data: any) => {
      let template
      if (data.type === "GRAPHIC") {
        template = await loadGraphicTemplate(data)
      } else if (data.type === "PRESENTATION") {
        template = await loadPresentationTemplate(data)
      } else if (data.type === "VIDEO") {
        template = await loadVideoTemplate(data)
      }
      //   @ts-ignore
      setScenes(template.scenes)
      //   @ts-ignore
      setCurrentDesign(template.design)
    },
    [editor]
  )

  const handleInputFileRefClick = () => {
    inputFileRef.current?.click()
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files![0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (res) => {
        const result = res.target!.result as string
        const design = JSON.parse(result)
        handleImportTemplate(design)
      }
      reader.onerror = (err) => {
        console.log(err)
      }

      reader.readAsText(file)
    }
  }

  return (
    <header className="ar-topbar">
      <div className="ar-topbar-brand" title="AgentResolve">
        <Logo size={26} />
      </div>

      <div className="ar-menu-island">
        <input
          multiple={false}
          onChange={handleFileInput}
          type="file"
          id="file"
          ref={inputFileRef}
          style={{ display: "none" }}
        />
        <button className="ar-menu-btn" onClick={handleInputFileRefClick}>
          <Icons.Uploads size={16} />
          <span className="ar-menu-label">{t("navbar.import")}</span>
        </button>

        <span className="ar-menu-divider" />

        <div style={{ position: "relative" }}>
          <button
            className="ar-menu-btn"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              setExportMenuOpen((open) => !open)
            }}
          >
            <Download size={16} />
            <span className="ar-menu-label">{t("navbar.export")}</span>
            <IconCaret />
          </button>
          {exportMenuOpen && (
            <div className="ar-dropdown" onClick={(e) => e.stopPropagation()}>
              <button
                className="ar-dropdown-item"
                onClick={() => {
                  setExportMenuOpen(false)
                  makeDownloadTemplate()
                }}
              >
                {t("navbar.downloadDesign")}
              </button>
              {editorType === "VIDEO" && (
                <button
                  className="ar-dropdown-item"
                  onClick={() => {
                    setExportMenuOpen(false)
                    setDisplayPreview(true)
                  }}
                >
                  {t("navbar.renderVideo")}
                </button>
              )}
            </div>
          )}
        </div>

        <span className="ar-menu-divider" />

        <button className="ar-menu-btn" onClick={saveSnapshot} title={t("navbar.snapshot")}>
          <IconCamera />
          <span className="ar-menu-label">{t("navbar.snapshot")}</span>
        </button>
      </div>

      <div className="ar-topbar-center">
        <DesignTitle />
      </div>

      <div className="ar-topbar-right">
        <button className="ar-btn ghost" onClick={() => setEditorType("NONE")}>
          {t("navbar.backHome")}
        </button>
        <button
          className="ar-btn"
          onClick={() => window.open("https://veditor.agenteresolve.com.br/billing/plans", "_blank")}
        >
          {t("navbar.plans")}
        </button>
        <button className="ar-btn primary" onClick={() => setDisplayPreview(true)}>
          <Play size={16} />
          {t("navbar.preview")}
        </button>
      </div>
    </header>
  )
}
